import { useEffect, useState } from "react";
import { KpiCard } from "@/components/KpiCard";
import { AttendanceStatusBadge } from "@/components/StatusBadge";
import {
  Users, UserCheck, UserMinus, FileCheck2, AlertTriangle, MapPinned,
  Plus, FileSearch, MapPin, Bot, ChevronRight, ArrowUpRight, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar, Legend,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { reportsApi, TenantDashboard } from "@/lib/reports-api";
import { sitesApi, SiteSummary } from "@/lib/sites-api";
import { pickedIds, pickedLabel, siteOptions } from "@/lib/picker-options";
import { SearchMultiSelect } from "@/components/SearchMultiSelect";
import { rangeToQuery } from "@/lib/date-range";
import { attendanceHint, attendanceRatePct, rangeDayCount } from "@/lib/dashboard-metrics";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { formatTimeIn } from "@/lib/datetime";
import { useTenantTimezone } from "@/hooks/useTenantTimezone";
import { useAnchoredDateRange } from "@/hooks/useAnchoredDateRange";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import type { AttendanceStatus } from "@/lib/attendance-api";
import type { AttendanceStatus as UiAttendanceStatus } from "@/data/mock";

/** Backend status → the label the shared badge component expects. */
const STATUS_LABELS: Record<AttendanceStatus, UiAttendanceStatus> = {
  PRESENT: "Present",
  CHECKED_IN: "Checked In",
  CHECKED_OUT: "Checked Out",
  PARTIAL: "Partial",
  EXCEPTION: "Exception",
  MISSED: "Missed",
  OUTSIDE_GEOFENCE: "Outside Geofence",
  PENDING_REVIEW: "Pending Review",
};

/** Slice colours live in the frontend; the API returns values only. */
const STATUS_COLORS: Record<string, string> = {
  PRESENT: "hsl(142 71% 38%)",
  PARTIAL: "hsl(38 92% 50%)",
  EXCEPTION: "hsl(0 72% 51%)",
  MISSED: "hsl(215 16% 47%)",
};

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState<TenantDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Defaults to today so the initial view matches the KPI spec exactly
  // ("Checked in today"); tile labels adapt once the range widens. "Today" is
  // the *tenant's* today — see useAnchoredDateRange.
  const tz = useTenantTimezone();
  const { range, setRange, preset, selectPreset } = useAnchoredDateRange("today");
  // Several sites may be picked (FEAT-2026-030); empty means all.
  const [siteFilter, setSiteFilter] = useState<string[]>([]);
  const [sites, setSites] = useState<SiteSummary[]>([]);

  useEffect(() => {
    sitesApi.list({ pageSize: 200 }).then((r) => setSites(r.data)).catch(() => { /* non-fatal */ });
  }, []);

  // Depend on the primitives, not the object: a new `range` reference with
  // identical values must not refetch.
  const { from: rangeFrom, to: rangeTo } = range;

  useEffect(() => {
    // Guard: a reversed custom range would 400 — let the user fix it instead
    // of flashing an error on every keystroke.
    if (rangeFrom > rangeTo) return;

    let cancelled = false;
    setLoading(true);
    reportsApi
      .tenantDashboard({
        ...rangeToQuery({ from: rangeFrom, to: rangeTo }),
        siteIds: pickedIds(siteFilter),
      })
      .then((d) => { if (!cancelled) { setData(d); setError(null); } })
      .catch((e) => {
        if (cancelled) return;
        const msg = e instanceof ApiError ? e.message : "Failed to load dashboard";
        setError(msg);
        toast.error(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [rangeFrom, rangeTo, siteFilter]);

  if (loading && !data) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <Card className="flex flex-col items-center justify-center gap-3 p-12 text-center">
        <AlertTriangle className="h-8 w-8 text-destructive" />
        <div>
          <h3 className="font-display font-semibold">Couldn't load the dashboard</h3>
          <p className="mt-1 text-sm text-muted-foreground">{error}</p>
        </div>
        <Button size="sm" onClick={() => window.location.reload()}>Try again</Button>
      </Card>
    );
  }

  if (!data) return null;

  const { kpis, dailyTrend, statusBreakdown, siteAttendance } = data;
  // Only a single-day range supports a "% of workforce" claim: over a range
  // `checkedInToday` counts employee-days (one record per employee per day),
  // which can't be divided by a point-in-time headcount. Null = don't claim a
  // percentage. Also guards /0 on an empty tenant.
  const attendanceRate = attendanceRatePct(range, kpis.checkedInToday, kpis.totalEmployees);
  const rangeDays = rangeDayCount(range);
  const firstName = user?.name?.split(" ")[0] ?? "there";
  const siteName = siteFilter.length ? pickedLabel(siteFilter, siteOptions(sites), "") : null;

  return (
    <div className="space-y-6">
      {/* Hero strip */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-hero p-6 text-white shadow-elegant">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: "radial-gradient(circle at 80% 20%, hsl(199 89% 48% / 0.6) 0, transparent 40%)",
        }} />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-white/60">
              {greeting()}, {firstName}
            </div>
            <h2 className="mt-1 font-display text-2xl font-bold tracking-tight">
              {kpis.totalEmployees === 0 ? (
                <>No employees yet — <span className="text-primary-glow">add your team to get started</span></>
              ) : (
                attendanceRate !== null ? (
                  <>Workforce is operating <span className="text-primary-glow">at {attendanceRate}% attendance</span></>
                ) : (
                  <><span className="text-primary-glow">{kpis.checkedInToday.toLocaleString()} attendance days</span> recorded across {rangeDays} days</>
                )
              )}
            </h2>
            <p className="mt-1 text-sm text-white/70">
              {kpis.exceptions} exception{kpis.exceptions === 1 ? "" : "s"} to review ·{" "}
              {kpis.pendingRequests} request{kpis.pendingRequests === 1 ? "" : "s"} in queue ·{" "}
              {kpis.activeSites} site{kpis.activeSites === 1 ? "" : "s"} live
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => navigate("/requests")}>
              Review Pending Requests
              <ArrowUpRight className="ml-1.5 h-4 w-4" />
            </Button>
            <Button size="sm" className="bg-white/10 hover:bg-white/20 text-white border border-white/20" onClick={() => navigate("/assistant")}>
              <Bot className="mr-1.5 h-4 w-4" />
              Ask AI Assistant
            </Button>
          </div>
        </div>
      </div>

      {/* Filters (FEAT-2026-006b). The range narrows attendance figures and
          every chart; point-in-time tiles (employees / sites / pending) follow
          only the site filter. */}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <DateRangeFilter value={range} preset={preset} onSelectPreset={selectPreset} onChange={setRange} />

          <div className="w-52">
            <Label className="text-xs text-muted-foreground">Site</Label>
            <SearchMultiSelect
              label="Site"
              values={siteFilter}
              onChange={setSiteFilter}
              options={siteOptions(sites)}
              allLabel="All sites"
              noun={{ one: "site", many: "sites" }}
              className="mt-1 h-9"
            />
          </div>
          {loading && (
            <div className="ml-auto flex items-center text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            </div>
          )}
        </div>
      </Card>

      {/* KPIs — no deltas: we have no historical baseline to compare against,
          and inventing one is exactly what this rewrite removes. */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <KpiCard label="Total Employees" value={kpis.totalEmployees.toLocaleString()} hint="Active" icon={Users} />
        <KpiCard label="Checked In" value={kpis.checkedInToday.toLocaleString()} hint={attendanceHint(range, kpis.checkedInToday, kpis.totalEmployees)} icon={UserCheck} tone="success" />
        <KpiCard label="Checked Out" value={kpis.checkedOutToday.toLocaleString()} hint={attendanceRate !== null ? "Completed shifts" : `across ${rangeDays} days`} icon={UserMinus} tone="info" />
        <KpiCard label="Pending Requests" value={kpis.pendingRequests} hint="Awaiting approval" icon={FileCheck2} tone="warning" />
        <KpiCard label="Exceptions" value={kpis.exceptions} hint="Needs review" icon={AlertTriangle} tone="destructive" />
        <KpiCard label="Active Sites" value={kpis.activeSites} hint="Currently active" icon={MapPinned} />
      </div>

      {/* Charts row */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-semibold">Daily attendance trend</h3>
              <p className="text-xs text-muted-foreground">
                {data.meta.trendFrom} → {data.meta.trendTo} · Present · Exceptions · Missed
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate("/reports")}>
              View report <ChevronRight className="ml-1 h-3 w-3" />
            </Button>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyTrend} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="gPresent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(217 91% 50%)" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(217 91% 50%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                />
                <Area type="monotone" dataKey="present" stroke="hsl(217 91% 50%)" strokeWidth={2.5} fill="url(#gPresent)" />
                <Area type="monotone" dataKey="exception" stroke="hsl(0 72% 51%)" strokeWidth={2} fill="transparent" />
                <Area type="monotone" dataKey="missed" stroke="hsl(38 92% 50%)" strokeWidth={2} fill="transparent" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5">
          <div className="mb-4">
            <h3 className="font-display font-semibold">Attendance breakdown</h3>
            <p className="text-xs text-muted-foreground">Status mix</p>
          </div>
          {statusBreakdown.length === 0 ? (
            <div className="flex h-48 items-center justify-center text-center text-xs text-muted-foreground">
              No attendance recorded for this period.
            </div>
          ) : (
            <>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={statusBreakdown} dataKey="value" nameKey="label" innerRadius={48} outerRadius={72} paddingAngle={2} strokeWidth={0}>
                      {statusBreakdown.map((s) => (
                        <Cell key={s.status} fill={STATUS_COLORS[s.status] ?? "hsl(215 16% 47%)"} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1.5">
                {statusBreakdown.map((s) => (
                  <div key={s.status} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ background: STATUS_COLORS[s.status] ?? "hsl(215 16% 47%)" }} />
                      <span className="text-muted-foreground">{s.label}</span>
                    </div>
                    <span className="font-medium tabular-nums">{s.value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Site comparison + Quick actions */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-semibold">Site-wise attendance snapshot</h3>
              <p className="text-xs text-muted-foreground">{siteName ?? "Across active sites"}</p>
            </div>
          </div>
          {siteAttendance.length === 0 ? (
            <div className="flex h-64 items-center justify-center text-center text-xs text-muted-foreground">
              No site attendance recorded for this period.
            </div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={siteAttendance} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="site" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="present" fill="hsl(217 91% 50%)" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="exception" fill="hsl(0 72% 51%)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card className="p-5">
          <h3 className="font-display font-semibold mb-4">Quick actions</h3>
          <div className="grid gap-2">
            {[
              { label: "Review Pending Requests", icon: FileCheck2, to: "/requests" },
              { label: "Add Employee", icon: Plus, to: "/employees" },
              { label: "View Attendance Logs", icon: FileSearch, to: "/attendance" },
              { label: "Configure Site", icon: MapPin, to: "/sites" },
              { label: "Open AI Assistant", icon: Bot, to: "/assistant" },
            ].map((a) => (
              <button key={a.label} onClick={() => navigate(a.to)} className="flex items-center justify-between rounded-lg border bg-secondary/30 p-3 text-left text-sm hover:bg-secondary hover:border-primary/30 transition-all">
                <span className="flex items-center gap-2.5">
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <a.icon className="h-3.5 w-3.5" />
                  </span>
                  {a.label}
                </span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
            ))}
          </div>
        </Card>
      </div>

      {/* Recent activity + alerts + pending */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold">Recent check-ins / check-outs</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate("/attendance")}>View all <ChevronRight className="ml-1 h-3 w-3" /></Button>
          </div>
          {data.recentActivity.length === 0 ? (
            <div className="py-8 text-center text-xs text-muted-foreground">
              No check-ins for this period — activity appears here as employees clock in.
            </div>
          ) : (
            <div className="space-y-1">
              {data.recentActivity.map((r) => (
                <div key={r.id} className="flex items-center gap-3 rounded-lg p-2.5 hover:bg-secondary/50 transition-colors">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-primary text-[10px] font-semibold text-white">
                    {r.employeeName.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium">{r.employeeName}</span>
                      <span className="text-xs text-muted-foreground">· {r.employeeEmpCode}</span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {r.siteName ?? "—"} · In {formatTimeIn(r.checkInAt, tz)} · Out {formatTimeIn(r.checkOutAt, tz)}
                    </div>
                  </div>
                  <AttendanceStatusBadge status={STATUS_LABELS[r.status]} />
                </div>
              ))}
            </div>
          )}
        </Card>

        <div className="space-y-4">
          <Card className="p-5">
            <h3 className="font-display font-semibold mb-3">Exception alerts</h3>
            {data.exceptionAlerts.length === 0 ? (
              <div className="rounded-lg border border-dashed p-4 text-center text-xs text-muted-foreground">
                No exceptions flagged.
              </div>
            ) : (
              <div className="space-y-2">
                {data.exceptionAlerts.map((a) => (
                  <div key={a.id} className="flex gap-2.5 rounded-lg border-l-2 border-destructive bg-destructive/5 p-2.5">
                    <AlertTriangle className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
                    <div className="min-w-0">
                      <div className="text-xs font-medium leading-snug">
                        {a.employeeName} · {STATUS_LABELS[a.status]}
                      </div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground line-clamp-2">
                        {a.flaggedReason ?? `${a.siteName ?? "No site"} · ${a.date}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-semibold">Pending approvals</h3>
              {kpis.pendingRequests > 0 && (
                <span className="rounded-full bg-warning/10 px-2 py-0.5 text-[10px] font-semibold text-warning">
                  {kpis.pendingRequests} total
                </span>
              )}
            </div>
            {data.pendingApprovals.length === 0 ? (
              <div className="rounded-lg border border-dashed p-4 text-center text-xs text-muted-foreground">
                Nothing awaiting approval.
              </div>
            ) : (
              <div className="space-y-2">
                {data.pendingApprovals.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => navigate("/requests")}
                    className="flex w-full items-center justify-between rounded-lg p-2 text-left hover:bg-secondary/50 transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="text-xs font-medium truncate">{p.employeeName}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{p.type} · {p.date}</div>
                    </div>
                    <span className="rounded-full bg-warning/10 px-2 py-0.5 text-[10px] font-medium text-warning">Pending</span>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
